import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import ConfirmationModal from "../../Common/ConfirmationModal";
import API from "../../../util/api";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const View = styled.div`
  flex: 1 1 0;
  min-width: 0;
  background: white;
  padding: 1.5em;
`;

const DetailTable = styled.table`
  ${adminTable};
  margin-bottom: 2em;
  font-size: 14px;

  tbody td {
    ${adminTableDataCell};
    padding: 0.75em 1em;
    vertical-align: top;
  }

  tbody tr:nth-child(odd) td:first-child,
  tbody tr:nth-child(odd) td:last-child {
    background: #f9f9f9;
  }

  tbody tr:nth-child(even) td:first-child,
  tbody tr:nth-child(even) td:last-child {
    background: #ffffff;
  }

  td:first-child {
    font-weight: bold;
    width: 30%;
  }
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin: 1em 0;
  label {
    font-size: 14px;
    margin-right: 8px;
    color: ${({ colors }) => colors.labelText};
    font-weight: bold;
  }
  select, input {
    padding: 5px 10px;
    font-size: 14px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 5px;
  }
  input { width: 200px; }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;

    select,
    input {
      width: 100%;
    }
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
`;

const OrgTable = styled.table`
  ${adminTable};
  min-width: 680px;

  thead th {
    ${adminTableHeaderCell};
    background: #fff;
    border-bottom: 1px solid #ccc;
    position: relative;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 8px 10px;
    vertical-align: top;
  }
  .SelectButton {
    margin-left: 0.5em;
    font-size: 0.85em;
    cursor: pointer;
    color: ${({ colors }) => colors.buttonBackground};
    border-bottom: 1px dotted ${({ colors }) => colors.buttonBackground};
    &:hover { text-decoration: underline; }
  }

  .sort-icons {
      position: absolute;
      right: 2px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      flex-direction: column;
      gap: 2px;
      font-size: 10px;

      span {
        cursor: pointer;
        line-height: 1;
        color: #9CA3AF;
        display: block;
      }

      .active {
        color: #A855F7;
      }
    }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5em;
  margin-top: 1em;
  button {
    padding: 5px 10px;
    border: 1px solid ${({ colors }) => colors.border};
    background: ${({ colors }) => colors.cardBackground};
    border-radius: 5px;
    cursor: pointer;
    &:disabled {
      background: ${({ colors }) => colors.disabledBackground};
      cursor: not-allowed;
    }
    &:hover:not(:disabled) {
      background: ${({ colors }) => colors.buttonBackground};
      color: white;
    }
  }
`;

export default function ChangeSchoolContent({
  colors,
  invoiceId,
  schoolFrom,
  onCancel,
  onComplete
}) {
  const from = schoolFrom;

  // organization search state
  const [search, setSearch]               = useState("");
  const [itemsPerPage, setItemsPerPage]   = useState(10);
  const [page, setPage]                   = useState(0);
  const [orderBy, setOrderBy]             = useState(null);
  const [order, setOrder]                 = useState(null);
  const [rows, setRows]                   = useState([]);
  const [total, setTotal]                 = useState(0);
  const [loading, setLoading]             = useState(false);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await API.searchOrganizations({
        is_advance_search: 0,
        search,
        order_by: orderBy,
        order,
        page,
        items_per_page: itemsPerPage,
      });
      setRows(resp.organizations || []);
      setTotal(resp.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, orderBy, order, page, itemsPerPage]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  // sorting
  const handleSort = (key) => {
    const next = orderBy === key && order === "ASC" ? "DESC" : "ASC";
    setOrderBy(key);
    setOrder(next);
    setPage(0);
  };

  // filtering
  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return rows.filter(r => r.org_name.toLowerCase().includes(term));
  }, [rows, search]);

  // selection & change flow
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultModal, setResultModal] = useState({
    show: false, title: "", message: "", status: "Success"
  });

  const handleChangeClick = () => setConfirmOpen(true);
  const handleConfirm     = async () => {
    setConfirmOpen(false);
    try {
      const resp = await API.changeSchool({
        invoice_id: invoiceId,
        school_id:  from.school_id,
        organization_id: selectedOrg.org_id,
      });
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setResultModal({
        show:    true,
        title:   status === "Failed" ? "Error" : "Changed",
        message: resp.message || (status === "Failed" ? "Change failed." : "School changed successfully."),
        status
      });
    } catch (err) {
      setResultModal({
        show:    true,
        title:   "Error",
        message: err.message || "Change failed.",
        status:  "Failed"
      });
    }
  };

  const finish = () => {
    setResultModal(m => ({ ...m, show: false, status: "Success" }));
    onComplete();
  };

  if (!from) return <p>Loading…</p>;

  return (
    <View>
      {/* Confirmations */}
      <ConfirmationModal
        show={confirmOpen}
        title="Confirm Change"
        message={`Change “${from.org_name}” to “${selectedOrg?.org_name}”?`}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />
      <ConfirmationModal
        show={resultModal.show}
        title={resultModal.title}
        message={resultModal.message}
        onCancel={finish}
        onConfirm={finish}
        status={resultModal.status}
        single
      />

      {/* School From */}
      <h1>School From:</h1>
      <DetailTable>
        <tbody>
          {[
            ["Name", from.org_name],
            ["Address", `${from.street}\n${from.city}, ${from.state} ${from.zip}`],
            ["Phone", from.phone],
            ["Type", from.org_type_desc],
            ["NCES School ID", from.nces_school_id || "—"],
            ["NCES District ID", from.nces_district_id || "—"],
            ["Private School ID", from.private_school_id || "—"],
            ["Source", from.source || "—"],
          ].map(([label, value], i) => (
            <tr key={i}>
              <td>{label}</td>
              <td style={{ whiteSpace: "pre-wrap" }}>{value}</td>
            </tr>
          ))}
        </tbody>
      </DetailTable>

      {/* School To */}
      <h1>School To:</h1>
      {selectedOrg ? (
        <>
          <DetailTable>
            <tbody>
              {[
                ["Name", selectedOrg.org_name],
                ["Address", `${selectedOrg.street}\n${selectedOrg.city}, ${selectedOrg.state} ${selectedOrg.zip}`],
                ["Phone", selectedOrg.phone],
                ["Type", selectedOrg.org_type_desc],
                ["NCES School ID", selectedOrg.nces_school_id || "—"],
                ["NCES District ID", selectedOrg.nces_district_id || "—"],
                ["Private School ID", selectedOrg.private_school_id || "—"],
                ["Source", selectedOrg.source || "—"],
              ].map(([label, value], i) => (
                <tr key={i}>
                  <td>{label}</td>
                  <td style={{ whiteSpace: "pre-wrap" }}>{value}</td>
                </tr>
              ))}
            </tbody>
          </DetailTable>

          <button
            style={{
              marginTop: "1em",
              padding: "0.6em 1.2em",
              background: colors.buttonBackground,
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
            onClick={handleChangeClick}
          >
            Change
          </button>
          <a
            href="#"
            onClick={e => { e.preventDefault(); onCancel(); }}
            style={{
              marginLeft: "1em",
              lineHeight: "2.2em",
              color: colors.buttonBackground,
            }}
          >
            Cancel
          </a>
        </>
      ) : (
        <>
          <Controls colors={colors}>
            <div>
              <label>Show</label>
              <select
                value={itemsPerPage}
                onChange={e => {
                  setItemsPerPage(Number(e.target.value));
                  setPage(0);
                }}
              >
                {[10,25,50,100].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <label style={{ marginLeft: 5 }}>entries</label>
            </div>
            <div>
              <label>Search:</label>
              <input
                type="text"
                value={search}
                placeholder="Type to filter…"
                onChange={e => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </div>
          </Controls>

          <TableWrapper>
            <OrgTable colors={colors}>
              <thead>
                <tr>
                  {[
                    ["org_name","Org Name"],
                    ["street",  "Street"],
                    ["city",    "City"],
                    ["state",   "State"],
                    ["zip",     "Zip"]
                  ].map(([key,label]) => {
                    const isAscActive = orderBy===key && order==="ASC";
                    const isDescActive = orderBy===key && order==="DESC";
                    const defaultColor = "#9CA3AF";
                    const activeColor = "#A855F7";
                    return (
                      <th key={key}>
                        {label}
                        <div className="sort-icons">
                          <span
                            onClick={() => handleSort(key)}
                            className={isAscActive ? "active" : ""}
                            style={{
                              color: isAscActive ? activeColor : defaultColor,
                              display: isDescActive ? "none" : "block"
                            }}
                          >
                            ▲
                          </span>
                          <span
                            onClick={() => handleSort(key)}
                            className={isDescActive ? "active" : ""}
                            style={{
                              color: isDescActive ? activeColor : defaultColor,
                              display: isAscActive ? "none" : "block"
                            }}
                          >
                            ▼
                          </span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filtered?.map(org => (
                  <tr key={org.org_id}>
                    <td>
                      {org.org_name}
                      <span
                        className="SelectButton"
                        onClick={() => setSelectedOrg(org)}
                      >
                        Select
                      </span>
                    </td>
                    <td>{org.street}</td>
                    <td>{org.city}</td>
                    <td>{org.state}</td>
                    <td>{org.zip}</td>
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>
                      No organizations found.
                    </td>
                  </tr>
                )}
              </tbody>
            </OrgTable>
          </TableWrapper>

          <Pagination colors={colors}>
            <button
              onClick={() => setPage(p => Math.max(p-1, 0))}
              disabled={page===0}
            >Previous</button>
            <button
              onClick={() => setPage(p => p+1)}
              disabled={(page+1)*itemsPerPage >= total}
            >Next</button>
          </Pagination>
        </>
      )}
    </View>
  );
}
